import {
  readWeightLimits, readCart, productWeight, weightProgress,
  weightLimitError, smallerSizeMessage, maximumAdditionalQuantity,
} from '@theme/product-weight-limits';

class CAProductOrderLimit extends HTMLElement {
  #controller;
  #refreshToken = 0;
  #grams = null;
  #variantPending = false;
  #limit;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    [this.#limit] = readWeightLimits(this);
    const form = this.closest('form');
    const section = this.closest('.shopify-section, dialog');
    document.addEventListener('cart:update', () => this.refresh(), { signal });
    window.addEventListener('focus', () => this.refresh(), { signal });
    form?.addEventListener('input', () => this.syncControls(), { signal });
    form?.addEventListener('quantity-selector:update', () => this.syncControls(), { signal });
    form?.addEventListener('weight-limit:error', (event) => {
      this.showError(event.detail);
      this.refresh();
    }, { signal });
    section?.addEventListener('variant:selected', (event) => {
      const picker = event.target.closest('variant-picker');
      if (String(picker?.dataset.productId) !== String(this.#limit.productId)) return;
      this.#variantPending = true;
      this.syncControls();
    }, { signal });
    section?.addEventListener('variant:update', (event) => {
      if (String(event.detail.data.productId) !== String(this.#limit.productId)) return;
      this.#variantPending = false;
      // Let the product form finish replacing the button and selected variant ID.
      queueMicrotask(() => { if (this.isConnected) this.syncControls(); });
    }, { signal });
    this.refresh();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#refreshToken++;
  }

  showError(message) {
    this.dataset.blocked = 'true';
    const controls = this.closest('form')?.querySelector('[data-weight-limit-controls]');
    if (controls) controls.disabled = true;
    const limit = this.#limit;
    this.querySelector('[data-weight-limit-status]').textContent = message.startsWith(limit.message)
      ? message.slice(limit.message.length).trim()
      : message;
  }

  syncControls() {
    const form = this.closest('form');
    const quantity = form?.querySelector('[name="quantity"]');
    const controls = form?.querySelector('[data-weight-limit-controls]');
    if (!quantity || !controls) return;
    const selector = quantity.closest('quantity-selector-component');
    const plus = selector.querySelector('[name="plus"]');
    const minus = selector.querySelector('[name="minus"]');
    const status = this.querySelector('[data-weight-limit-status]');
    const limit = this.#limit;

    if (this.#grams === null || this.#variantPending) {
      controls.disabled = true;
      quantity.disabled = true;
      plus.disabled = true;
      minus.disabled = true;
      status.textContent = limit.checkingText;
      return;
    }

    try {
      const variantId = form.querySelector('[name="id"]').value;
      let maximum = maximumAdditionalQuantity(limit, this.#grams, variantId);
      if (!quantity.hasAttribute('data-weight-limit-max')) {
        quantity.dataset.quantityRuleMax = quantity.max;
        quantity.dataset.weightLimitMax = '';
      }
      if (quantity.dataset.quantityRuleMax) maximum = Math.min(maximum, Number(quantity.dataset.quantityRuleMax));
      quantity.max = String(maximum);
      const minimum = Number(quantity.min || 1);
      const value = Number(quantity.value);
      const step = Number(quantity.step || 1);
      const reached = Math.round(this.#grams) >= limit.limitGrams;
      quantity.disabled = maximum < minimum;
      plus.disabled = maximum < minimum || value + step > maximum;
      minus.disabled = maximum < minimum || value <= minimum;
      controls.disabled = maximum < minimum || value < minimum || !quantity.checkValidity() || !Number.isInteger(value);
      quantity.setAttribute('aria-invalid', String(!quantity.disabled && controls.disabled));
      this.dataset.blocked = String(reached || controls.disabled);

      if (reached) status.textContent = weightProgress(limit, this.#grams);
      else if (maximum < minimum) status.textContent = smallerSizeMessage(limit, this.#grams);
      else if (value > maximum) status.textContent = weightLimitError(limit, this.#grams);
      else status.textContent = weightProgress(limit, this.#grams);
    } catch {
      controls.disabled = true;
      quantity.disabled = true;
      plus.disabled = true;
      minus.disabled = true;
      this.showError(limit.unavailableText);
    }
  }

  async refresh() {
    const token = ++this.#refreshToken;
    const limit = this.#limit;
    if (!limit) return;
    this.#grams = null;
    this.syncControls();
    try {
      const cart = await readCart();
      if (token !== this.#refreshToken || !this.isConnected) return;
      this.#grams = productWeight(limit, cart.items);
      this.syncControls();
    } catch {
      if (token === this.#refreshToken && this.isConnected) this.showError(limit.unavailableText);
    }
  }
}

if (!customElements.get('ca-product-order-limit')) {
  customElements.define('ca-product-order-limit', CAProductOrderLimit);
}
